import { z } from 'zod';
import { metricsTypeEnum } from '@features/ai-insights';

const aiInsightsSchema = z
  .array(
    z.object({
      dimensionName: z.string().nullable(),
      summary: z.array(z.string()),
      metricsType: metricsTypeEnum,
    })
  )
  .optional();

export const rslReportChannelSchema = z.object({
  id: z.number(),
  rslReportId: z.number(),
  channelType: z.enum(['online', 'in_store', 'combined']),

  projectedActualTransactions: z.number().nullable(),
  projectedIncrementalTransactions: z.number().nullable(),

  projectedActualTransactionValuePerImpression: z.number().nullable(),
  projectedIncrementalTransactionValuePerImpression: z.number().nullable(),

  transactionRateLift: z.number().nullable(),
  transactionSizeLift: z.number().nullable(),
  penetrationLift: z.number().nullable(),

  transactionSize: z.number().nullable(),
  transactionRate: z.number().nullable(),

  penetration: z.number().nullable(),

  salesRate: z.number().nullable().optional(),

  controlPenetration: z.number().nullable().optional().default(null),
  controlTransactionSize: z.number().nullable().optional().default(null),
  controlTransactionRate: z.number().nullable().optional().default(null),

  incrementalSalesLift: z.number().nullable(),
  incrementalSalesLiftConfidenceLevel: z.number().nullable(),
  projectedIncrementalSales: z.number().nullable(),
  totalProjectedSales: z.number().nullable(),
});

export const rslDimensionSchema = z.object({
  id: z.number(),
  rslReportId: z.number(),

  dimensionName: z.string(),
  dimensionValue: z.string(),
  salesChannelType: z.enum(['online', 'in_store', 'combined']).nullable(),

  impressions: z.number(),
  uniqueUsers: z.number().nullable(),
  matchedForAttribution: z.number().nullable(),

  incrementalSalesLift: z.number().nullable(),
  liftConfidenceLevel: z.number().nullable(),
  projectedIncrementalSales: z.number().nullable(),
  totalProjectedSales: z.number().nullable(),
  salesLiftIndex: z.number().nullable(),

  transactionSize: z.number().nullable(),
  transactionRate: z.number().nullable(),

  penetration: z.number().nullable(),
  penetrationLift: z.number().nullable(),

  salesRate: z.number().nullable().optional(),

  controlPenetration: z.number().nullable().optional().default(null),
  controlTransactionSize: z.number().nullable().optional().default(null),
  controlTransactionRate: z.number().nullable().optional().default(null),

  transactionRateLift: z.number().nullable(),
  transactionSizeLift: z.number().nullable(),

  projectedActualTransactionValuePerImpressionIndex: z.number().nullable(),
  projectedIncrementalTransactionValuePerImpressionIndex: z.number().nullable(),

  statisticallySignificant: z.boolean(),

  incrementalVisitLift: z.number().nullable().optional().default(null),
  incrementalVisitLiftConfidenceLevel: z
    .number()
    .nullable()
    .optional()
    .default(null),
  totalProjectedVisits: z.number().nullable().optional().default(null),
  projectedIncrementalVisits: z.number().nullable().optional().default(null),
  totalProjectedVisitsPerImpression: z
    .number()
    .nullable()
    .optional()
    .default(null),
  projectedIncrementalVisitsPerImpression: z
    .number()
    .nullable()
    .optional()
    .default(null),
  incrementalVisitsPerImpressionIndex: z
    .number()
    .nullable()
    .optional()
    .default(null),
  totalVisitsPerImpressionIndex: z.number().nullable().optional().default(null),
  incrementalVisitLiftIndex: z.number().nullable().optional().default(null),
});

export const rslReportSchema = z
  .object({
    id: z.number(),
    campaignId: z.number(),
    reportDate: z.string(),
    status: z.enum(['pending', 'approved']),

    impressions: z.number(),
    uniqueUsers: z.number().nullable(),
    matchedForAttribution: z.number().nullable(),

    incrementalSalesLift: z.number().nullable(),
    incrementalSalesLiftConfidenceLevel: z.number().nullable(),

    projectedIncrementalSales: z.number().nullable(),
    totalProjectedSales: z.number().nullable(),

    projectedActualTransactions: z.number().nullable(),
    projectedIncrementalTransactions: z.number().nullable(),

    transactionSize: z.number().nullable(),
    transactionRate: z.number().nullable(),

    projectedActualTransactionValuePerImpression: z.number().nullable(),
    projectedIncrementalTransactionValuePerImpression: z.number().nullable(),

    transactionRateLift: z.number().nullable(),
    transactionSizeLift: z.number().nullable(),

    penetration: z.number().nullable(),
    penetrationLift: z.number().nullable(),

    salesRate: z.number().nullable().optional(),

    controlPenetration: z.number().nullable().optional().default(null),
    controlTransactionSize: z.number().nullable().optional().default(null),
    controlTransactionRate: z.number().nullable().optional().default(null),

    channels: z.array(rslReportChannelSchema),
    dimensions: z.array(rslDimensionSchema),
    aiInsights: aiInsightsSchema,
    visitAIInsights: aiInsightsSchema,

    incrementalVisitLift: z.number().nullable().optional().default(null),
    incrementalVisitLiftConfidenceLevel: z
      .number()
      .nullable()
      .optional()
      .default(null),
    totalProjectedVisits: z.number().nullable().optional().default(null),
    projectedIncrementalVisits: z.number().nullable().optional().default(null),
    totalProjectedVisitsPerImpression: z
      .number()
      .nullable()
      .optional()
      .default(null),
    projectedIncrementalVisitsPerImpression: z
      .number()
      .nullable()
      .optional()
      .default(null),
  })
  .transform(r => ({
    ...r,
    hasVisitMetrics: r.totalProjectedVisits !== null,
  }));

export const rslReportsResponseSchema = z.object({
  data: z.object({
    reports: z.array(rslReportSchema),
  }),
});

export type RslReportChannel = z.infer<typeof rslReportChannelSchema>;
export type RslDimension = z.infer<typeof rslDimensionSchema>;
export type RslReport = z.infer<typeof rslReportSchema>;
export type RslReportsResponse = z.infer<typeof rslReportsResponseSchema>;
